import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { EquityTradingServiceStrategy } from './equity-trading-service-strategy.service';

import { TransactionType, OrderType, DeskRouteTimeZone, DeskRouteCode, OrderDuration, AccountType, Account, TradingDayInfo } from '../../models/common/trading-common.model';
import { TradingEntitlements } from './../../models/common/trading-entitlements';
import { TRADING_PLATFORM } from '../../utils/trading-constants';
import { Injectable } from "@angular/core";

@Injectable()
export class ProfessionalEquityTradingServiceStrategy extends EquityTradingServiceStrategy {
    private static SUPPORTED_PLATFORM = TRADING_PLATFORM.PROFESSIONAL;

    support(platform: string): boolean {
        return ProfessionalEquityTradingServiceStrategy.SUPPORTED_PLATFORM === platform;
    }

    public accountTypes(account: Account, isNonDollar: boolean, transactionType: TransactionType, isGTCOrder: boolean, symAccountType: AccountType): AccountType[] {
        if (!!account && !!transactionType && !!symAccountType) {
            return this.getAccountTypes(account,isNonDollar,transactionType,symAccountType);
        } else if (!!account && !!transactionType && !symAccountType) {
            // Following logic is per the reverse engineering flow chart and compliance rules
            if (account.custodianCd === 'BNYM') {
                return [AccountType.PRINCIPAL, AccountType.INCOME];
            }
            return this.getAccountTypesWithoutSymAccountType(account,isNonDollar,transactionType);
        } else if (!!account && !transactionType && !symAccountType) {
            if (account.custodianCd === 'BNYM') {
                return [AccountType.PRINCIPAL, AccountType.INCOME];
            } else {
                return this.getDefaultAccountTypes(account, isNonDollar);
            }
        }
    }

    private getAccountTypes(account: Account, isNonDollar: boolean, transactionType: TransactionType,symAccountType: AccountType): AccountType[] {
        if (account.custodianCd === 'BNYM') {
            if (transactionType ===  TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_NOT_IN_ACCOUNT) {
                return [symAccountType];
            }
        }
            if (transactionType === TransactionType.BUY_TO_COVER || transactionType === TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_ALL || transactionType === TransactionType.SELL_SHORT) {
            return [symAccountType];
        }

        return this.getDefaultAccountTypes(account, isNonDollar);
    }

    private getAccountTypesWithoutSymAccountType(account: Account, isNonDollar: boolean, transactionType: TransactionType): AccountType[] {
        
        if ( transactionType === TransactionType.BUY_TO_COVER ) {
            return [AccountType.SHORT];
        } else if ( transactionType === TransactionType.SELL_NOT_IN_ACCOUNT ) {
            if (!!account.acctCatgCd && account.acctCatgCd.trim() === AccountType.COD) {
                if (isNonDollar) {
                    return [AccountType.COD];
                } else {
                    return [AccountType.CASH, AccountType.COD];
                }
            } else {
                return [AccountType.CASH];
            }
        } else if (
            transactionType === TransactionType.SELL_FROM_PORTFOLIO
        ) {
            return this.getDefaultAccountTypes(account, isNonDollar);
        } else if (
            transactionType === TransactionType.SELL_SHORT
            || transactionType === TransactionType.SHORT_SALE_EXEMPT
        ) {
            // The transaction type list can only show Sell Short and Short Sale Exempt if the account is
            // margin-enabled or avg price / allocation account, so don't worry about checking the COD or
            // margin-enabled flag here
            if ( transactionType === TransactionType.SELL_SHORT) {  // NXCPIT-8217
                return [AccountType.SHORT];
            }

            return [AccountType.CASH, AccountType.MARGIN, AccountType.SHORT];
        }

        return this.getDefaultAccountTypes(account, isNonDollar);
    }

    public getTransactionTypes(account: Account, selectedSession: string): TransactionType[] {
        const data: TransactionType[] = [];
        const isMarginEnabledAccount: boolean = account.acctMarginInd === 'Y' || account.applicantMarginCd === '001';
        const isAvgPriceOrAllocationAcct: boolean =
            account.acctNum.substring(0, 3) === '950' || account.acctNum.substring(3, 6) === '897' || account.acctNum.substring(3, 6) === '896';
        const isBankCustodyAcct: boolean = account.custodianCd === 'BNYM';
        data.push(TransactionType.BUY);
        if (isBankCustodyAcct) {
            data.push(TransactionType.SELL_FROM_PORTFOLIO);
            data.push(TransactionType.SELL_NOT_IN_ACCOUNT);
        } else {
            if (isMarginEnabledAccount || isAvgPriceOrAllocationAcct) {
                data.push(TransactionType.BUY_TO_COVER);
            }
            if (account.acctCatgCd && account.acctCatgCd.trim() !== AccountType.COD) {
                // COD accounts cannot  do Sell All
                data.push(TransactionType.SELL_ALL);
            }

            data.push(TransactionType.SELL_FROM_PORTFOLIO);
            data.push(TransactionType.SELL_NOT_IN_ACCOUNT);

            if ( isMarginEnabledAccount || isAvgPriceOrAllocationAcct ) {
                data.push(TransactionType.SELL_SHORT);
                data.push(TransactionType.SHORT_SALE_EXEMPT);
            }
        }

        return data;
    }

    public getOrderTypes(entitlements: TradingEntitlements, isNonDollar: boolean, selectedSession: string,
                          hasCodeValueExchange: boolean, qty: number, dollarOrFractional: boolean, transtype: TransactionType): OrderType[] {
        const orderTypes: OrderType[] = [];
        console.log(selectedSession);
        // during extended hours, user can trade only in LIMIT order.
        if (selectedSession === 'EXTENDED') {
            orderTypes.push(OrderType.LIMIT);
            return orderTypes;
        }

        if (dollarOrFractional) {
            orderTypes.push(OrderType.MARKET);
            if ( ((transtype === TransactionType.SELL_FROM_PORTFOLIO || transtype === TransactionType.SELL_ALL) && qty >= 1)
                  || transtype === TransactionType.BUY ) {
              orderTypes.push(OrderType.LIMIT);
            }
            return orderTypes;
        }

        if (isNonDollar) {
            orderTypes.push(OrderType.MARKET);
            orderTypes.push(OrderType.LIMIT);
            orderTypes.push(OrderType.EXECUTE_AT_CLOSE);
            if (hasCodeValueExchange) {
                orderTypes.push(OrderType.LIMIT_ON_CLOSE);
            }

            return orderTypes;
        }
        orderTypes.push(OrderType.MARKET);
        orderTypes.push(OrderType.LIMIT);
        orderTypes.push(OrderType.STOP_LIMIT);
        orderTypes.push(OrderType.STOP);
        if (entitlements.isTrailingStopEnabled) {
            orderTypes.push(OrderType.TRAILING_STOP_DOLLAR);
            orderTypes.push(OrderType.TRAILING_STOP_PCT);
            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_DOLLAR);
            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_PCT);
        }
        orderTypes.push(OrderType.EXECUTE_AT_CLOSE);
        orderTypes.push(OrderType.LIMIT_ON_CLOSE);
        return orderTypes;
    }

    public getDurations(selectedSession: string, tradingDayInfo: TradingDayInfo, isNonDollar: boolean, transactionType: TransactionType,
                        orderType?: string, ordQty?: number, dollarOrFractional?: boolean): OrderDuration[] {
        if (dollarOrFractional) {
          return [OrderDuration.DAY];
        }

        if (tradingDayInfo && selectedSession === 'EXTENDED') {
            return this.getDurationForExtendedHours(tradingDayInfo);
        }

        if (isNonDollar && orderType) {
            switch (orderType) {
                case OrderType.MARKET: {
                    return [OrderDuration.DAY];
                }
                case OrderType.LIMIT:
                case OrderType.EXECUTE_AT_CLOSE:
                case OrderType.LIMIT_ON_CLOSE: {
                    return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD];
                }
                default: {
                    return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD];
                }
            }
        }

        if (orderType) {
            return this.getDurationByOrderType(orderType,ordQty,transactionType);
        } else {
            return this.getDefaultOrderDuration(ordQty,transactionType);
        }
    }

    private getDurationForExtendedHours(tradingDayInfo: TradingDayInfo) : OrderDuration[] {
        if (tradingDayInfo?.isECNPreTradingHrs) {
            return [OrderDuration.PREMKT];
        }
        if (tradingDayInfo?.isECNPostTradingHrs) {
            return [OrderDuration.AFTMKT];
        }
    }

    private getDurationByOrderType(orderType:string,ordQty:number,transactionType: TransactionType) {
        switch (orderType) {
            case OrderType.MARKET: {
                return [OrderDuration.DAY, OrderDuration.OPG];
            }
            case OrderType.LIMIT: {
                return this.getDefaultOrderDuration(ordQty,transactionType);
            }
            case OrderType.STOP_LIMIT: {
                return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
            }
            case OrderType.STOP: {
                return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
            }
            case OrderType.EXECUTE_AT_CLOSE: {
                return [OrderDuration.DAY];
            }
            case OrderType.LIMIT_ON_CLOSE: {
                if (ordQty < 100) {
                    return [OrderDuration.DAY];
                } else {
                    return [OrderDuration.DAY, OrderDuration.IOC, OrderDuration.FOK];
                }
            }
            case OrderType.TRAILING_STOP_DOLLAR: {
                return this.getDurationForTrailingOrderType(transactionType);
            }
            case OrderType.TRAILING_STOP_PCT: {
                return this.getDurationForTrailingOrderType(transactionType);
            }
            case OrderType.TRAILING_STOP_LIMIT_DOLLAR: {
                return this.getDurationForTrailingOrderType(transactionType);
            }
            case OrderType.TRAILING_STOP_LIMIT_PCT: {
                return this.getDurationForTrailingOrderType(transactionType);
            }
            default: {
                return this.getDefaultOrderDuration(ordQty,transactionType);
            }
        }
    }

    private getDurationForTrailingOrderType(transactionType:TransactionType): OrderDuration[]{
        return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
    }

    private getDefaultOrderDuration(ordQty,transactionType:TransactionType): OrderDuration[] {
        if (ordQty < 100) {
            return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY, OrderDuration.OPG] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.OPG, OrderDuration.GTM];
        } else {
            return transactionType === TransactionType.SELL_SHORT ? [OrderDuration.DAY, OrderDuration.IOC, OrderDuration.FOK, OrderDuration.OPG] : [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.IOC, OrderDuration.FOK, OrderDuration.OPG, OrderDuration.GTM];
        }
    }

    public getDeskRouteTimeZone(): DeskRouteTimeZone[] {
        return [DeskRouteTimeZone.CT, DeskRouteTimeZone.PT, DeskRouteTimeZone.MT, DeskRouteTimeZone.ET];
    }

    public getDeskCode(): DeskRouteCode[] {
        return [DeskRouteCode.A, DeskRouteCode.AR, DeskRouteCode.B, DeskRouteCode.C,
             DeskRouteCode.D, DeskRouteCode.IS, DeskRouteCode.IN, DeskRouteCode.O, DeskRouteCode.PF,
             DeskRouteCode.PT, DeskRouteCode.PR, DeskRouteCode.S,
             DeskRouteCode.T, DeskRouteCode.TR, DeskRouteCode.SW, DeskRouteCode.EC, DeskRouteCode.CR];
    }

    public getCodeValuesExchangeList(): Observable<any> {
        const codeValuesExchangeListUrl: string = this.getBaseUrl() + '/code-values-exchange-list';
        return this.http.get(codeValuesExchangeListUrl, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            })
        });
    }

    public getCodeValuesFloorInstructions(): Observable<any> {
        const codeValuesExchangeListUrl: string = this.getBaseUrl() + '/code-values-floor-instruction';
        return this.http.get(codeValuesExchangeListUrl, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            })
        });
    }

    private getDefaultAccountTypes(account: Account, isNonDollar: boolean): AccountType[] {
        const isMarginEnabledAccount: boolean = account.acctMarginInd === 'Y' || account.applicantMarginCd === '001';
        const isBankCustodyAcct: boolean = account.custodianCd === 'BNYM';

        if (isBankCustodyAcct) {
            return [AccountType.PRINCIPAL, AccountType.INCOME];
        }
        if ( account.acctCatgCd && account.acctCatgCd.trim() === AccountType.COD ) {
            if ( isNonDollar ) {
                return [AccountType.COD];
            }

            return [AccountType.CASH, AccountType.COD];
        } else if ( isMarginEnabledAccount ) {
            return [AccountType.CASH, AccountType.MARGIN];
        }
        return [AccountType.CASH];
    }
}
